import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SessionStats } from "@/features/sessions";
import {
  CLASSIFICATIONS,
  classificationColor,
} from "@/features/sessions/classification";
import { useChartTheme } from "@/features/charts/theme";
import { ChartTooltip } from "@/features/charts/tooltip";
import { ResponsivePie } from "@nivo/pie";
import { useEffect, useRef } from "react";
import { bySliceSize, commonPieConfig } from "./utils";
import { PieLegend } from "./pie-legend";
import { useTranslation } from "@/features/utils/i18n";

interface ClassificationProps {
  stats: SessionStats | undefined;
}

export function Classification({ stats }: ClassificationProps) {
  const { t } = useTranslation("dashboard");
  const { theme } = useChartTheme();
  const isFirstRender = useRef(true);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  if (!stats) return null;

  const getTagLabel = (tag: string): string => {
    const key = `classification.tags.${tag}` as const;
    const translated = t(key);
    // If translation key is returned as-is, use formatted tag name
    return translated === key ? tag.replace("_", " ") : translated;
  };

  const counts = new Map(stats.sessions_by_tag.map((d) => [d.tag, d.count]));
  const total = stats.sessions_by_tag.reduce((sum, d) => sum + d.count, 0);

  // Every classification the backend reported — the known ones with their fixed
  // colours, then anything it may start tagging before this list catches up —
  // biggest first (the fixed order only breaks ties).
  const data = [
    ...CLASSIFICATIONS.map(({ id, color }) => ({
      id,
      label: getTagLabel(id),
      value: counts.get(id) || 0,
      color,
    })),
    ...stats.sessions_by_tag
      .filter((d) => !CLASSIFICATIONS.some((c) => c.id === d.tag))
      .map((d) => ({
        id: d.tag,
        label: getTagLabel(d.tag),
        value: d.count,
        color: classificationColor(d.tag),
      })),
  ]
    .filter((d) => d.value > 0)
    .sort(bySliceSize);

  const share = (value: number) =>
    total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("classification.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <div className="space-y-4">
            {/* The categories are named in the legend below, so the donut carries
                no arc labels: they were the ones being clipped by the card. */}
            <div className="relative h-[200px]">
              <ResponsivePie
                {...commonPieConfig}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                enableArcLinkLabels={false}
                data={data}
                theme={theme}
                animate={isFirstRender.current}
                motionConfig="gentle"
                tooltip={({ datum }) => (
                  <ChartTooltip
                    title={String(datum.label)}
                    rows={[
                      {
                        color: datum.color,
                        label: t("classification.sessions"),
                        value: `${datum.value} · ${share(datum.value)}%`,
                      },
                    ]}
                  />
                )}
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold">{total}</span>
                <span className="text-xs text-muted-foreground">
                  {t("classification.sessions")}
                </span>
              </div>
            </div>

            <PieLegend data={data} total={total} />
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            {t("classification.noData")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
